// StageQuestionnaire — candidate-facing questionnaire page.
// Route: /stage-questionnaire/:jobId/:candidateId/:stageId

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle, Loader2, AlertCircle } from "lucide-react";

import { API_ROOT } from "../../config/base";
import useSchoolName from "./useSchoolName";

// Same origin, under this app's path: origin + '/hiring', so the calls below
// go to /hiring/api/... . REACT_APP_API_URL still overrides.
const API_BASE = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/api$/, "")
  : window.location.origin + API_ROOT;

export default function StageQuestionnaire() {
  const { schoolName } = useSchoolName();
  const { jobId, candidateId, stageId } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState("loading"); // loading | form | submitting | done | error | already_done
  const [data, setData]   = useState(null);       // { title, instructions, jobTitle, candidateName, questions }
  const [answers, setAnswers] = useState({});      // { [questionId]: { writtenAnswer?, selectedOption? } }

  const [error, setError]     = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/questionnaire/public/${jobId}/${candidateId}/${stageId}`);
        const json = await r.json();
        if (json.alreadyDone) { setState("already_done"); return; }
        if (!json.success)    { setError(json.message || "Could not load questionnaire."); setState("error"); return; }
        setData(json.data);
        // Init answers map
        const init = {};
        (json.data.questions || []).forEach(q => {
          init[q.questionId] = q.questionType === "mcq" ? { selectedOption: null } : { writtenAnswer: "" };
        });
        setAnswers(init);
        setState("form");
      } catch {
        setError("Network error. Please check your connection and try again.");
        setState("error");
      }
    })();
  }, [jobId, candidateId, stageId]);

  const setAnswer = (questionId, patch) => {
    setAnswers(a => ({ ...a, [questionId]: { ...a[questionId], ...patch } }));
  };

  const submit = async () => {
    // Validate — all questions answered
    const unanswered = (data?.questions || []).filter(q => {
      const a = answers[q.questionId];
      if (q.questionType === "mcq") return a?.selectedOption === null || a?.selectedOption === undefined;
      return !a?.writtenAnswer?.trim();
    });
    if (unanswered.length > 0) {
      alert(`Please answer all questions before submitting. (${unanswered.length} unanswered)`);
      return;
    }

    setState("submitting");
    try {
      const payload = Object.entries(answers).map(([questionId, ans]) => ({ questionId, ...ans }));
      const r = await fetch(`${API_BASE}/api/questionnaire/public/${jobId}/${candidateId}/${stageId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers: payload }),
      });
      const json = await r.json();
      if (!json.success) { setError(json.message); setState("error"); return; }

      setState("done");
    } catch {
      setError("Submission failed. Please try again.");
      setState("form");
    }
  };

  // ── Loading ───────────────────────────────────────────────────────
  if (state === "loading") return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex items-center gap-3 text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span>Loading questionnaire…</span>
      </div>
    </div>
  );

  // ── Already done ──────────────────────────────────────────────────
  if (state === "already_done") return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white rounded-2xl shadow-sm border p-8 max-w-md w-full text-center">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Already submitted</h2>
        <p className="text-gray-500 text-sm">You have already completed this questionnaire. Your responses are recorded.</p>
      </div>
    </div>
  );

  // ── Error ─────────────────────────────────────────────────────────
  if (state === "error") return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white rounded-2xl shadow-sm border p-8 max-w-md w-full text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
        <p className="text-gray-500 text-sm">{error}</p>
      </div>
    </div>
  );

  // ── Done ──────────────────────────────────────────────────────────
  if (state === "done") return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white rounded-2xl shadow-sm border p-8 max-w-md w-full text-center">
        <img src={`${process.env.PUBLIC_URL}/assets/dps-crest.png`} alt={schoolName} className="h-12 mx-auto mb-3" />
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Questionnaire submitted!</h2>
        {/* No score shown, deliberately. An eligibility questionnaire is a
            gate, not an exam — telling a candidate "9 out of 10" invites
            them to wonder which answer cost a point and whether to answer
            differently, and honest answers are the whole value of the form.
            The score lives in the console, for the hiring team. */}
        <p className="text-gray-500 text-sm">
          Thank you{data?.candidateName ? `, ${data.candidateName}` : ""}. Your responses have been recorded.
          The hiring team will review them and be in touch.
        </p>
      </div>
    </div>
  );

  // ── Form ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header — crest first: this page is the school speaking. */}
        <div className="bg-white rounded-2xl border shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
            <img src={`${process.env.PUBLIC_URL}/assets/dps-crest.png`} alt="" className="h-10" />
            <p className="text-[11px] font-semibold tracking-widest text-gray-400 uppercase">{schoolName}</p>
          </div>
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">{data?.jobTitle}</p>
          <h1 className="text-2xl font-bold text-gray-900">{data?.title}</h1>
          {data?.instructions && (
            <p className="mt-2 text-sm text-gray-500 leading-relaxed">{data.instructions}</p>
          )}
        </div>

        {/* Questions */}
        {(data?.questions || []).map((q, idx) => (
          <div key={q.questionId} className="bg-white rounded-2xl border shadow-sm p-6 space-y-3">
            <div className="flex items-start gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center mt-0.5">
                {idx + 1}
              </span>
              <p className="text-sm font-medium text-gray-800 leading-relaxed">{q.text}</p>
            </div>

            {q.questionType === "mcq" ? (
              <div className="space-y-2 pl-9">
                {(q.options || []).filter(Boolean).map((opt, oi) => (
                  <label key={oi} className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="radio"
                      name={`q-${q.questionId}`}
                      value={oi}
                      checked={answers[q.questionId]?.selectedOption === oi}
                      onChange={() => setAnswer(q.questionId, { selectedOption: oi })}
                      className="accent-blue-600 w-4 h-4"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900">{opt}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="pl-9">
                <textarea
                  rows={4}
                  className="w-full border rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Type your answer here…"
                  value={answers[q.questionId]?.writtenAnswer || ""}
                  onChange={e => setAnswer(q.questionId, { writtenAnswer: e.target.value })}
                />
              </div>
            )}
          </div>
        ))}

        {/* Submit */}
        <button
          onClick={submit}
          disabled={state === "submitting"}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-60 transition"
        >
          {state === "submitting" ? (
            <><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</>
          ) : (
            "Submit Questionnaire"
          )}
        </button>

        <p className="text-center text-xs text-gray-400 pb-6">
          Once submitted, you cannot change your answers.
        </p>
      </div>
    </div>
  );
}
